import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { errText } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);
const REQUEST_TIMEOUT = 15000;
const SESSION_CHECK_FRESH_MS = 5 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=checking, null=no auth, obj=auth
  const [empresa, setEmpresa] = useState(null);
  const [authError, setAuthError] = useState("");
  const [checking, setChecking] = useState(false);
  const inFlight = useRef(null);
  const mounted = useRef(true);
  const sessionVersion = useRef(0);
  const lastSuccessfulCheck = useRef(null);

  const refreshMe = useCallback(() => {
    if (inFlight.current) return inFlight.current;
    const version = sessionVersion.current;
    const current = () => mounted.current && version === sessionVersion.current;
    if (mounted.current) setChecking(true);
    const request = (async () => {
      try {
        let response;
        try {
          response = await api.get("/auth/me", { timeout: REQUEST_TIMEOUT });
        } catch (firstError) {
          if (firstError?.response?.status !== 401) throw firstError;
          // Reuse the HttpOnly refresh cookie once; never retry in a loop.
          await api.post("/auth/refresh", null, { timeout: REQUEST_TIMEOUT });
          response = await api.get("/auth/me", { timeout: REQUEST_TIMEOUT });
        }
        const data = response.data;
        if (!data?.usuario || typeof data.usuario !== "object" || !data.usuario.id) {
          throw new Error("Respuesta de sesión incompleta");
        }
        if (current()) {
          lastSuccessfulCheck.current = Date.now();
          setUser(data.usuario);
          setEmpresa(data.empresa);
          setAuthError("");
        }
        return data;
      } catch (error) {
        if (current()) {
          if (error?.response?.status === 401) {
            setUser(null);
            setEmpresa(null);
            setAuthError("");
          } else {
            // A network/server failure does not invalidate an existing session.
            setAuthError("No pude comprobar tu cuenta. Revisá tu conexión y volvé a intentar.");
          }
        }
        return null;
      } finally {
        if (current()) setChecking(false);
      }
    })();
    inFlight.current = request;
    request.then(() => {
      if (inFlight.current === request) inFlight.current = null;
    });
    return request;
  }, []);

  useEffect(() => {
    mounted.current = true;
    refreshMe();
    return () => { mounted.current = false; };
  }, [refreshMe]);

  useEffect(() => {
    if (!user?.id) return;
    const alVolver = () => {
      if (document.visibilityState === "hidden" || lastSuccessfulCheck.current === null) return;
      if (Date.now() - lastSuccessfulCheck.current > SESSION_CHECK_FRESH_MS) refreshMe();
    };
    window.addEventListener("focus", alVolver);
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      window.removeEventListener("focus", alVolver);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [user?.id, refreshMe]);

  const register = async (nombre, email, password) => {
    sessionVersion.current += 1;
    const { data } = await api.post("/auth/register", { nombre, email, password });
    sessionVersion.current += 1;
    lastSuccessfulCheck.current = Date.now();
    setUser(data.usuario);
    setEmpresa(data.empresa);
    setAuthError("");
    setChecking(false);
    return data;
  };

  const login = async (email, password) => {
    sessionVersion.current += 1;
    const { data } = await api.post("/auth/login", { email, password });
    sessionVersion.current += 1;
    lastSuccessfulCheck.current = Date.now();
    setUser(data.usuario);
    setEmpresa(data.empresa);
    setAuthError("");
    setChecking(false);
    return data;
  };

  const logout = async () => {
    sessionVersion.current += 1;
    lastSuccessfulCheck.current = null;
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    setEmpresa(null);
    setAuthError("");
    setChecking(false);
  };

  return (
    <AuthCtx.Provider value={{ user, empresa, setUser, setEmpresa, refreshMe, register, login, logout, authError, errText }}>
      {user === undefined && authError ? (
        <div className="min-h-screen bg-negro flex items-center justify-center px-6" data-testid="auth-initial-error">
          <div className="card-dark p-6 max-w-md space-y-4 text-crema">
            <p role="alert">{authError}</p>
            <button type="button" className="btn-dorado min-h-[44px]" onClick={refreshMe} disabled={checking} data-testid="auth-reintentar">
              {checking ? "COMPROBANDO…" : "VOLVER A INTENTAR"}
            </button>
          </div>
        </div>
      ) : children}
    </AuthCtx.Provider>
  );
}
